/* @jsx h */
import { defineComponent, h, ref, onMounted, computed } from "vue";
import {
  SpecialPriceReward,
  Product,
  PromotionRequirement,
  Service,
} from "@lemmonhq/types";
import { api } from "@app/data/index";
import { getProductIdsFromCategories } from "@lemmonhq/orders";
import { useAuth } from "@app/vue/providers/Auth";

export type SpecialPriceRewardEditorProps = {
  value: SpecialPriceReward;
  onChange: (reward: SpecialPriceReward) => void;
  requirements: PromotionRequirement[];
};

export const SpecialPriceRewardEditor =
  defineComponent<SpecialPriceRewardEditorProps>({
    name: "SpecialPriceRewardEditor",
    props: ["value", "onChange", "requirements"],
    setup(props) {
      const auth = useAuth();
      const products = ref<Product[]>([]);
      const services = ref<Service[]>([]);
      const loading = ref(false);
      const searchQuery = ref("");

      const loadData = async () => {
        if (loading.value || products.value.length > 0) return;
        try {
          loading.value = true;
          const [productsData, servicesData] = await Promise.all([
            api.getProducts(),
            api.getServices(),
          ]);
          products.value = productsData;
          services.value = servicesData;
        } catch (error) {
          console.error("Error loading data:", error);
        } finally {
          loading.value = false;
        }
      };

      onMounted(() => {
        loadData();
      });

      // Get eligible product IDs from requirements
      const eligibleProductIds = computed(() => {
        const productIds = new Set<number>();

        props.requirements.forEach((req) => {
          if (req.type === "product") {
            req.product_ids.forEach((id) => productIds.add(id));
          } else if (req.type === "product_category") {
            const ids = getProductIdsFromCategories(
              services.value,
              req.category_ids,
            );
            ids.forEach((id) => productIds.add(id));
          }
        });

        return Array.from(productIds);
      });

      // Filter products to only show eligible ones
      const eligibleProducts = computed(() => {
        if (eligibleProductIds.value.length === 0) {
          return [];
        }
        return products.value.filter((p) =>
          eligibleProductIds.value.includes(p.id),
        );
      });

      const updateProductPrice = (productId: number, price: number) => {
        const newPrices = { ...props.value.prices };

        if (price > 0) {
          // Set the price
          newPrices[productId] = price;
        } else {
          // Remove the product if price is 0 or empty
          delete newPrices[productId];
        }

        props.onChange({
          ...props.value,
          prices: newPrices,
        });
      };

      const getSpecialPrice = (productId: number) => {
        return props.value.prices?.[productId] || 0;
      };

      const filteredProducts = computed(() => {
        if (!searchQuery.value.trim()) {
          return eligibleProducts.value;
        }
        const query = searchQuery.value.toLowerCase();
        return eligibleProducts.value.filter((product) =>
          product.name.toLowerCase().includes(query),
        );
      });

      const productsWithPricing = computed(() => {
        return Object.keys(props.value.prices || {}).length;
      });

      return () => (
        <div class="card border bg-base-200">
          <div class="card-body p-4">
            <h4 class="card-title text-accent text-lg flex items-center gap-2">
              <div class="w-3 h-3 bg-warning rounded-full animate-pulse"></div>
              Special Price Reward
            </h4>

            <div class="space-y-4 mt-4">
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">
                    Set Special Prices for Products
                  </span>
                  <span class="label-text-alt text-base-content/60">
                    {productsWithPricing.value} product
                    {productsWithPricing.value !== 1 ? "s" : ""} configured
                  </span>
                </label>

                <input
                  type="text"
                  value={searchQuery.value}
                  onInput={(e) =>
                    (searchQuery.value = (e.target as HTMLInputElement).value)
                  }
                  class="input input-bordered focus:input-warning mb-3"
                  placeholder="Search products..."
                />

                {loading.value ? (
                  <div class="flex items-center justify-center py-8">
                    <span class="loading loading-spinner loading-lg text-warning"></span>
                  </div>
                ) : eligibleProductIds.value.length === 0 ? (
                  <div class="text-center py-8 text-base-content/60">
                    <div class="text-warning text-4xl mb-2">⚠️</div>
                    <div class="font-medium">
                      No products selected in requirements
                    </div>
                    <div class="text-sm mt-1">
                      Add product or category requirements above to select
                      products for special pricing
                    </div>
                  </div>
                ) : (
                  <div class="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {filteredProducts.value.length === 0 ? (
                      <div class="text-center py-8 text-base-content/60">
                        No products match your search
                      </div>
                    ) : (
                      filteredProducts.value.map((product) => {
                        const specialPrice = getSpecialPrice(product.id);
                        const originalPrice =
                          typeof product.price === "string"
                            ? parseFloat(product.price)
                            : product.price;
                        const savings =
                          originalPrice > 0 && specialPrice > 0
                            ? ((originalPrice - specialPrice) / originalPrice) *
                              100
                            : 0;
                        const hasSpecialPrice = specialPrice > 0;

                        return (
                          <div
                            key={product.id}
                            class={`p-3 rounded-lg border transition-all ${
                              hasSpecialPrice
                                ? "bg-warning/10 border-warning/40"
                                : "bg-base-200/50 "
                            }`}
                          >
                            <div class="flex items-center gap-3">
                              <div class="flex-1">
                                <div class="font-medium text-accent">
                                  {product.name}
                                </div>
                                <div class="text-sm text-base-content/60">
                                  Regular:{" "}
                                  {auth.user.value?.restaurant.currency ||
                                    "CHF"}{" "}
                                  {originalPrice.toFixed(2)}
                                </div>
                              </div>

                              <div class="flex items-center gap-2">
                                <div class="relative w-32">
                                  <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-base-content/50 text-xs">
                                    {auth.user.value?.restaurant.currency ||
                                      "CHF"}
                                  </span>
                                  <input
                                    type="text"
                                    value={
                                      specialPrice > 0
                                        ? specialPrice.toFixed(2)
                                        : ""
                                    }
                                    onBlur={(e) => {
                                      const value = parseFloat(
                                        (e.target as HTMLInputElement).value,
                                      );
                                      if (!isNaN(value) && value > 0) {
                                        updateProductPrice(product.id, value);
                                      } else {
                                        updateProductPrice(product.id, 0);
                                      }
                                    }}
                                    class={`input input-sm input-bordered focus:input-warning w-full pl-11 ${
                                      hasSpecialPrice ? "input-warning" : ""
                                    }`}
                                    placeholder="—"
                                  />
                                </div>

                                {savings !== 0 && specialPrice > 0 && (
                                  <div
                                    class={`badge badge-sm ${
                                      savings > 0
                                        ? "badge-success"
                                        : "badge-error"
                                    } gap-1 min-w-[60px]`}
                                  >
                                    {savings > 0 ? "↓" : "↑"}
                                    {Math.abs(savings).toFixed(0)}%
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

            <div class="alert alert-warning/20 border-warning/30 mt-4">
              <div class="flex items-center gap-2">
                <div class="text-warning text-xl">💰</div>
                <div>
                  <div class="font-medium text-warning">
                    Special Price Preview
                  </div>
                  <div class="text-sm text-warning/80">
                    {productsWithPricing.value === 0
                      ? "Enter special prices for products above"
                      : `${productsWithPricing.value} product${
                          productsWithPricing.value > 1 ? "s" : ""
                        } will have special pricing`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    },
  });
